import { defineStore } from 'pinia';
import { Auth } from 'aws-amplify';
import isEqual from 'lodash/isEqual';

import { setLocale, getBrowserLocale } from '../helpers/i18n';
import { baseGet } from '../helpers/api';
import { getFavorites, setFavorites } from './favorites';
import { getHierarchyByUser, getHierarchyPeersByUser } from './hierarchy';
import { getPatches } from './patches';
import { getGroups, getUserGroups } from './groups';
import { getPermissions, getUserLocations } from './permissions';
import { postConfigAPI, getConfigAPI, deleteConfigAPI } from './userSettings';
import { ref, shallowRef, computed, watch, nextTick } from 'vue';
import { unsetAssumedUserOrgCookies } from '../helpers/v2AuthLoader';
import { unsetAuthTokenCookies } from '../helpers/token_extraction';
import { buildUserNameToUse } from '../helpers/buildUserNameToUse';
import { getApiTokenCIMT } from '../helpers/ebos_token.js';

function getInitialTheme() {
  if (typeof window === 'undefined') return false;
  const localTheme = localStorage.getItem('theme');
  if (localTheme) return localTheme;
  const prefersDarkMode =
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDarkMode ? 'dark' : 'light';
}

export const useRootStore = defineStore('root', () => {
  /** @type {import('vue').Ref<Record<string, unknown> | null>} */
  const user = ref(null);
  const selectedViewBy = ref('locations');
  const selectedScopes = shallowRef([]);
  const patches = ref(null);
  const hierarchyByUser = ref(null);
  const hierarchyPeersByUser = ref(null);
  const assumingUser = ref(false);
  const onMobileApp = ref(false);
  const mobilePlatform = ref('');
  // permissions
  const permissions = shallowRef([]);
  const deniedPermissions = shallowRef([]);
  const groups = shallowRef([]);
  const userGroups = shallowRef([]);
  const failedToLoad = ref(false);
  // settings
  const theme = ref(getInitialTheme());
  const favoritePages = ref();
  const showNavBar = ref(true);
  const userSettings = ref(null);
  const userSettingsPromise = ref(false);

  const currentAppKey = ref(0);
  const chatOpened = ref(false);
  const chatGroupId = ref(null);
  const chatTimestamp = ref(null);
  const chatSenderId = ref(null);

  // getters
  const locations = computed(() => {
    // "myq safe" the locations. Use user.locations if you want the dirty locations lol
    return user.value?.locations?.filter((x) => /^\d+$/.test(x.value)) || [];
  });
  const org = computed(() => {
    return user.value?.organization;
  });
  /** @type {import('vue').ComputedRef<string>} */
  const orgId = computed(() => {
    return user.value?.organization?.orgId?.replace('org-', '');
  });
  const orgName = computed(() => {
    return user.value?.organization?.orgName;
  });
  const enterpriseClient = computed(() => {
    return user.value?.organization?.enterpriseClient;
  });
  const orgApps = computed(() => {
    return user.value?.organization?.apps;
  });
  /** @type {import('vue').ComputedRef<string>} */
  const userId = computed(() => {
    return user.value?.userId;
  });
  const userName = computed(() => {
    return user.value?.userName;
  });
  const isAdmin = computed(() => {
    return user.value?.qsrAdmin;
  });
  const isOrgAdmin = computed(() => {
    return !!userGroups.value?.find((g) => g.name === 'System Administrators');
  });
  const selectedLocations = computed(() => {
    let locs = [];
    if (selectedViewBy.value === 'locations')
      locs = selectedScopes.value.map((l) => l?.value);
    else locs = selectedScopes.value.flatMap((patch) => patch?.locations);
    locs = locs.filter((x) => x);
    return [...new Set(locs)];
  });

  const orgStartOfWeek = computed(() => {
    const dayToNumber = {
      Sunday: 0,
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
    };
    const day = user.value?.organization?.orgStartOfWeek;
    return dayToNumber[day] || 0;
  });
  const countryCode = computed(() => {
    return org.value?.countryCode?.toLowerCase() || 'us';
  });
  const language = computed(() => user.value?.language || getBrowserLocale());
  watch(
    language,
    (locale) => {
      setLocale(locale);
    },
    { immediate: true },
  );
  const geid = computed(() => user.value?.geid);
  const eid = computed(() => user.value?.eid);

  const hasEmployeeRole = computed(() => {
    const uGroups = userGroups?.value?.map((group) => group.name);
    if (
      uGroups.includes('Employee') ||
      uGroups.includes('employee') ||
      uGroups.includes('crew') ||
      uGroups.includes('Crew')
    )
      return true;
    return false;
  });

  const isCrewMember = computed(() => {
    if (org.value?.apps?.includes('crew_portal')) {
      if (permissions.value?.includes('crew_portal_experience')) {
        return hasEmployeeRole.value;
      }
    }
    return false;
  });

  const appKey = computed(() => `${currentAppKey.value}`);

  // actions
  function reset() {
    user.value = null;
    selectedViewBy.value = 'locations';
    selectedScopes.value = [];
    patches.value = null;
    hierarchyByUser.value = null;
    hierarchyPeersByUser.value = null;
    assumingUser.value = false;
    permissions.value = [];
    deniedPermissions.value = [];
    favoritePages.value = null;
    // remove local storage
    // localStorage.clear();
    localStorage.removeItem('user');
    localStorage.removeItem('selectedViewBy');
    localStorage.removeItem('selectedScopes');
    localStorage.removeItem('assumedUserID');
    localStorage.removeItem('multiOrgUserSelectedOrgId');
    localStorage.removeItem('assumedOrgId');
    localStorage.removeItem('onMobileApp');
    localStorage.removeItem('cimtToken');
    localStorage.removeItem('ebosLoginDone');

    unsetAssumedUserOrgCookies();
    unsetAuthTokenCookies();
  }
  async function reload() {
    await getUser();
    saveLocalStorage();
    // const permissionsPromise = loadPermissions();
    const hierarchyPromise = loadHierarchyByUser();
    const peersPromise = loadHierarchyPeersByUser();
    const groupsPromise = loadGroups();
    // const userGroupsPomise = loadUserGroups();
    // const locationPromise = loadUserLocations();
    // await everything above so that we can await this reload
    await Promise.all([
      // permissionsPromise,
      hierarchyPromise,
      peersPromise,
      // locationPromise,
      groupsPromise,
    ]);
    selectedViewBy.value = 'locations';
    // only select the non sold stores
    selectedScopes.value = locations.value.filter((l) => l.isActive);
    saveLocalStorage();
  }
  function saveLocalStorage() {
    // set the local storage so that we load fast as fuck for next time
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 48);
    const saveObj = {
      user: user.value,
      hierarchyByUser: hierarchyByUser.value,
      hierarchyPeersByUser: hierarchyPeersByUser.value,
      permissions: permissions.value,
      deniedPermissions: deniedPermissions.value,
      groups: groups.value,
      userGroups: userGroups.value,
      expiresAt,
    };
    localStorage.setItem('user', JSON.stringify(saveObj));
    localStorage.setItem('assumedOrgId', `org-${orgId.value}`);
  }
  function loadScopesFromLocalStorage() {
    const viewBy = localStorage.getItem('selectedViewBy');
    const scopes = localStorage.getItem('selectedScopes');
    if (viewBy) selectedViewBy.value = viewBy;
    if (scopes) {
      const parsed = JSON.parse(scopes);
      if (!isEqual(selectedScopes.value, parsed)) selectedScopes.value = parsed;
    }
  }
  function getAssumedOrgId() {
    const multiOrgUserSelectedOrgId = localStorage.getItem(
      'multiOrgUserSelectedOrgId',
    );
    const assumedOrgId = localStorage.getItem('assumedOrgId');
    // assuming a user overrides the multiOrgUserSelectedOrgId
    return multiOrgUserSelectedOrgId || assumedOrgId;
  }
  function loadFromLocalStorage() {
    // fuck it, we ball
    const userString = localStorage.getItem('user');

    if (userString) {
      try {
        const userObj = JSON.parse(userString);
        // console.log({
        //   userString,
        //   userObj,
        // });
        const userObjOrgId = userObj?.user?.organization?.orgId;
        const orgId = getAssumedOrgId();
        const orgIdsMatch = orgId ? orgId === userObjOrgId : true;
        // console.log({ orgId, userObjOrgId });
        if (new Date() < new Date(userObj.expiresAt) && orgIdsMatch) {
          user.value = userObj.user;
          selectedScopes.value = locations.value;
          hierarchyByUser.value = userObj.hierarchyByUser;
          hierarchyPeersByUser.value = userObj.hierarchyPeersByUser;
          permissions.value = userObj.permissions;
          deniedPermissions.value = userObj.deniedPermissions;
          groups.value = userObj.groups;
          userGroups.value = userObj.userGroups;
        } else {
          // console.log(
          //   'user local storage expired, no fast load this time, buddy',
          // );
        }
      } catch (e) {
        console.warn('failed fast load user', e);
        localStorage.clear();
      }
    }
  }
  async function getUser(idToken) {
    // console.log('getting user');
    const { payload } = idToken ?? {};
    const assumedUserID = localStorage.getItem('assumedUserID');
    if (assumedUserID) assumingUser.value = true;

    const regularUserID = payload?.sub || user.value?.userId;
    const targetUserID = assumedUserID || regularUserID;

    // assuming a user overrides the multiOrgUserSelectedOrgId
    const finalOrgId = getAssumedOrgId();

    // make the params for the user request
    const params = {
      userId: targetUserID,
    };
    if (finalOrgId) params.orgId = finalOrgId.replace('org-', '');

    const urlParams = new URLSearchParams(params);
    const requestURL = `user/info?${urlParams.toString()}`;
    const data = await baseGet(requestURL);

    // console.log({ data });

    const qsrAdmin = data.qsrAdmin;
    const multiOrgUserSelectedOrgId = localStorage.getItem(
      'multiOrgUserSelectedOrgId',
    );
    const filteredOrgs = data.organizations.filter((org) => !org.inactive);

    let organization;
    if (qsrAdmin) {
      organization =
        filteredOrgs.find((org) => org.orgId === finalOrgId) ||
        filteredOrgs.find((org) => !org.supportUser) ||
        filteredOrgs[0];
    } else if (multiOrgUserSelectedOrgId) {
      organization =
        filteredOrgs.find((org) => org.orgId === multiOrgUserSelectedOrgId) ||
        filteredOrgs[0];
    } else if (filteredOrgs.length === 1) {
      organization = filteredOrgs[0];
    }

    const email = payload?.email || user.value?.email;
    let locations;
    if (organization) {
      const loadUserLocs = async () => {
        await loadUserGroups(organization?.orgId, data.userId);
        locations = await loadUserLocations({
          user: data,
          orgId: organization?.orgId,
        });
      };
      await Promise.all([
        loadUserLocs(),
        loadPermissions(organization?.orgId, data.userId),
      ]);
    }

    user.value = {
      userId: data.userId,
      organization,
      displayData: data.displayData,
      organizations: data.organizations,
      locations,
      userName: buildUserNameToUse(data),
      qsrAdmin,
      email,
      geid: organization?.geid || data?.geid,
      eid: data?.eID,
      eidEmail: data.eidEmail,
      language: data.displayData.language,
      orgIds: data.orgIds,
      wfm: data.wfm,
    };
  }
  async function loadUserLocations({ user, orgId }) {
    const userId = user?.userId;
    if (!orgId || !userId) return [];
    try {
      const cleanedOrgId = orgId.replace('org-', '');
      const cleanedUserId = userId.replace('user-', '');
      const locs = await getUserLocations({
        orgId: cleanedOrgId,
        userId: cleanedUserId,
      });
      // check if the selected locations are valid. if not set them to the user locations
      if (
        !selectedLocations.value?.length ||
        selectedLocations.value?.some((x) => !locs.find((l) => l.value === x))
      ) {
        selectedViewBy.value = 'locations';
        // filter out all freak locations
        selectedScopes.value = locs.filter(
          (x) => x.isActive && /^\d+$/.test(x.value),
        );
      }
      return locs;
    } catch (e) {
      console.error('loadUserLocations', e);
    }
  }

  async function loadPermissions(orgId, userId) {
    const cleanedOrgId = orgId.replace('org-', '');
    const cleanedUserId = userId.replace('user-', '');
    const user = await getPermissions(cleanedOrgId, cleanedUserId);
    permissions.value = user?.permissions || [];
    deniedPermissions.value = user?.deniedPermissions || [];
    if (canAccessApp('candi_mt_beta') || canAccessApp('candi_mt')) {
      await getApiTokenCIMT(cleanedOrgId);
    }
  }
  async function loadPatches() {
    if (patches.value) return;
    try {
      patches.value = await getPatches(orgId.value, userId.value);
    } catch (e) {
      console.error('loadPatches', e);
    }
  }
  async function loadHierarchyByUser() {
    try {
      hierarchyByUser.value = await getHierarchyByUser({
        userId: userId.value,
        isAdmin: isAdmin.value,
        orgId: orgId.value,
      });
    } catch (e) {
      // console.error('loadHierarchyByUser', e);
      hierarchyByUser.value = null;
    }
  }
  async function loadHierarchyPeersByUser() {
    try {
      hierarchyPeersByUser.value = await getHierarchyPeersByUser({
        userId: userId.value,
        orgId: orgId.value,
        isAdmin: isAdmin.value,
      });
    } catch (e) {
      // console.error('getHierarchyPeersByUser', e);
      hierarchyPeersByUser.value = null;
    }
  }
  async function loadGroups() {
    try {
      groups.value = await getGroups(orgId.value);
    } catch (e) {
      console.error('loadGroups', e);
    }
  }
  /**
   * @param {string} orgId - The organization ID
   * @param {string} userId - The user ID
   */
  async function loadUserGroups(orgId, userId) {
    if (!orgId || !userId)
      throw new Error('loadUserGroups: missing orgId or userId');
    try {
      const cleanedOrgId = orgId.replace('org-', '');
      const cleanedUserId = userId.replace('user-', '');
      userGroups.value = await getUserGroups(cleanedOrgId, cleanedUserId);
    } catch (e) {
      console.error('loadGroups', e);
    }
  }
  async function hydrate(retryLimit = 2) {
    failedToLoad.value = false;
    // the rest of hydrate lives in this if statement
    const needsToHydrate = !user.value;
    if (!needsToHydrate) return;

    let idToken;
    try {
      // get jwt and id tokens out of the auth
      const currentSession = await Auth.currentSession();
      idToken = currentSession.idToken;

      if (!idToken)
        throw new Error('Invalid/Null ID Token encountered when hydrating');

      loadFromLocalStorage();
      loadScopesFromLocalStorage();
      await getUser(idToken);
      loadScopesFromLocalStorage();
      saveLocalStorage();

      const promises = [
        // loadPermissions(),
        loadHierarchyByUser(),
        loadHierarchyPeersByUser(),
        // loadUserLocations(),
        loadGroups(),
        // loadUserGroups(),
      ];
      await Promise.all(promises);
      saveLocalStorage();
    } catch (e) {
      console.error('HYDRATE : CRITICALLY FAILED', e);
      if (retryLimit > 0 && e.message === 'Network Error') {
        // console.log('HYDRATE : RETRYING');
        await hydrate(retryLimit - 1);
      } else {
        failedToLoad.value = true;
        throw e;
      }
    }
  }

  // favorite pages
  async function getFavoritePages() {
    if (favoritePages.value) return;
    favoritePages.value = await getFavorites(orgId.value, userId.value);
  }
  function setFavoritePages(favorites) {
    favoritePages.value = favorites;
    return setFavorites(orgId.value, userId.value, favorites);
  }

  // user settings
  async function getUserSettings(name) {
    if (!userSettings.value && !userSettingsPromise.value) {
      userSettingsPromise.value = getConfigAPI().then((savedSettings) => {
        userSettings.value = savedSettings || {};
        userSettingsPromise.value = false;
      });
    }
    if (userSettingsPromise.value) await userSettingsPromise.value;
    return userSettings.value?.[name];
  }
  async function updateUserSettings(name, value) {
    const resp = await postConfigAPI(name, value);
    if (!resp) return false;
    userSettings.value[name] = value;
    return true;
  }
  async function deleteUserSettings(name) {
    const resp = await deleteConfigAPI(name);
    if (!resp) return false;
    delete userSettings.value[name];
    return true;
  }
  function canAccessApp(app) {
    const val = app === 'customer_recovery' ? 'customer_feedback' : app;
    if (assumingUser.value && val === 'datapass') return false;
    // Product owners want to demo security to users that don't have security
    if (val === 'security' && isAdmin.value) return true;
    if (!!org.value && !!orgApps.value && !!orgApps.value.length) {
      return orgApps.value.some(
        (appName) => appName.toLowerCase() === val.toLowerCase(),
      );
    }
    return false;
  }

  function remountApp() {
    currentAppKey.value += 1;
  }

  watch(theme, () => {
    localStorage.setItem('theme', theme.value);
  });
  const darkThemes = ['dark', 'halloween'];
  const darkMode = computed(() => darkThemes.includes(theme.value));

  watch(selectedViewBy, () => {
    localStorage.setItem('selectedViewBy', selectedViewBy.value);
    if (selectedViewBy.value === 'patches') loadPatches();
  });

  watch(selectedScopes, () => {
    localStorage.setItem(
      'selectedScopes',
      JSON.stringify(selectedScopes.value),
    );
  });

  // send org id to mobile app
  watch(orgId, () => {
    if (assumingUser.value) return;
    if (!orgId.value) return;
    window.sendMessageToApp?.('changeClient', {
      orgId: orgId.value,
    });
  });

  function waitForUserToBeLoaded() {
    if (user.value) return Promise.resolve();
    let resolvePromise;
    const promise = new Promise((resolve) => {
      resolvePromise = resolve;
    });

    watch(user, () => {
      if (!user.value) return;
      nextTick(() => resolvePromise());
    });

    return promise;
  }

  return {
    user,
    userName,
    waitForUserToBeLoaded,
    selectedViewBy,
    selectedScopes,
    patches,
    hierarchyByUser,
    hierarchyPeersByUser,
    assumingUser,
    permissions,
    deniedPermissions,
    groups,
    userGroups,
    failedToLoad,
    theme,
    locations,
    favoritePages,
    onMobileApp,
    chatOpened,
    chatGroupId,
    chatTimestamp,
    chatSenderId,
    showNavBar,
    mobilePlatform,
    // getters
    org,
    orgId,
    orgName,
    enterpriseClient,
    countryCode,
    orgApps,
    userId,
    isAdmin,
    isOrgAdmin,
    selectedLocations,
    orgStartOfWeek,
    language,
    isCrewMember,
    hasEmployeeRole,
    geid,
    eid,
    appKey,
    darkMode,
    // actions
    reset,
    reload,
    hydrate,
    saveLocalStorage,
    getFavoritePages,
    setFavoritePages,
    loadPatches,
    loadUserLocations,
    getUserSettings,
    updateUserSettings,
    deleteUserSettings,
    canAccessApp,
    remountApp,
  };
});
